/** Port of ItineraryDetailsScreenStateMapper.kt.
 *
 * Isomorphic to the Kotlin on purpose: same emission order, same gates, same names.
 *
 * Upstream: ui/itinerary_details/ItineraryDetailsScreenStateMapper.kt
 * Spec: aidocs/context/android-renderer-spec.md §1D
 */
import { getDaysRange, type ItineraryDomain } from '../adapters/itinerary';
import { mapPrice, type Currency } from '../text/format';

export const STRINGS = {
  allMonths: 'All months',
  /** NOTE: singular "Day", and a PLAIN hyphen. Not "Days", not the en-dash. */
  day: (n: number) => `Day ${n}`,
  days: (a: number, b: number) => `Day ${a} - ${b}`,
} as const;

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const TOTAL_MONTHS = 12;

export type TimelinePosition = 'SINGLE' | 'TOP' | 'MIDDLE' | 'BOTTOM';

export type ItinerarySliderImage = {
  key: string;
  image: { prefix: string; suffix: string } | null;
  overlayPlay: boolean;
  placeholder?: boolean;
};

export type ItineraryItem =
  | { kind: 'imageSlider'; key: string; images: ItinerarySliderImage[] }
  /** SectionItem with style Title1 — 28px Bold, unlimited lines. */
  | { kind: 'titleSection'; key: string; text: string }
  | { kind: 'body'; key: string; html: string }
  | { kind: 'calendarRow'; key: string; text: string }
  | { kind: 'routeImage'; key: string; image: { prefix: string; suffix: string } }
  | { kind: 'timelineDay'; key: string; text: string; position: TimelinePosition }
  | {
      kind: 'timelineDayContent';
      key: string;
      image: { prefix: string; suffix: string } | null;
      subTitleHtml: string | null;
      bodyHtml: string | null;
      footerHtml: string | null;
      position: TimelinePosition;
    };

export interface ItineraryBottomBar {
  title: string | null;
  caption: string | null;
  buttonText: string | null;
  buttonPlacement: 'end' | 'center';
}

export interface ItineraryUiState {
  title: string;
  items: ItineraryItem[];
  bottomModel: ItineraryBottomBar | null;
  /** Toolbar menu: share only, and only when there is something to share. */
  showShare: boolean;
  notes: string[];
}

function positionFor(index: number, count: number): TimelinePosition {
  if (count === 1) return 'SINGLE';
  if (index === 0) return 'TOP';
  if (index === count - 1) return 'BOTTOM';
  return 'MIDDLE';
}

export function mapItineraryDetails(
  itinerary: ItineraryDomain,
  currency: Currency | null,
  online = true,
): ItineraryUiState {
  const items: ItineraryItem[] = [];
  const notes: string[] = [];

  // 1 — slider. Falls back to the cover image, then to a placeholder.
  const images: ItinerarySliderImage[] = [...itinerary.images]
    .sort((a, b) => a.sequence - b.sequence)
    .map((image) => ({
      key: String(image.id),
      image: image.image,
      overlayPlay: image.hasVideo && online,
    }));
  items.push({
    kind: 'imageSlider',
    key: 'slider',
    images:
      images.length > 0
        ? images
        : [
            {
              key: 'cover_image',
              image: itinerary.coverImage,
              overlayPlay: false,
              placeholder: itinerary.coverImage === null,
            },
          ],
  });

  // 2 — title, Title1
  items.push({ kind: 'titleSection', key: 'title', text: itinerary.name });

  // 3 — highlights, ELSE description. Strictly one or the other.
  // This is the fallback iOS does not have: the single most important divergence.
  if (itinerary.highlights) {
    items.push({ kind: 'body', key: 'highlights', html: itinerary.highlights });
  } else if (itinerary.description) {
    items.push({ kind: 'body', key: 'description', html: itinerary.description });
  }

  // 4 — months
  if (itinerary.months.length > 0) {
    const text =
      itinerary.months.length === TOTAL_MONTHS
        ? STRINGS.allMonths
        : itinerary.months.map((m) => MONTH_ABBR[m - 1] ?? '').filter(Boolean).join(', ');
    items.push({ kind: 'calendarRow', key: 'calendar', text: text.toUpperCase() });
  }

  // 5 — route map
  if (itinerary.routeMapImage) {
    items.push({ kind: 'routeImage', key: 'route_image', image: itinerary.routeMapImage });
  }

  // 6 — two items per day
  if (itinerary.days.length > 0) {
    const days = [...itinerary.days].sort((a, b) => a.sequence - b.sequence);
    days.forEach((day, index) => {
      let dayText: string;
      if (!itinerary.customDayTitles) {
        const [start, end] = getDaysRange(days, index);
        dayText = end === null ? STRINGS.day(start) : STRINGS.days(start, end);
      } else {
        dayText = day.title ?? '';
      }
      const position = positionFor(index, days.length);

      // Both items share the "timeline_day_" prefix upstream, so they collide on
      // uniqueProperty alone; Android is saved by comparing class + key. Key on both.
      items.push({ kind: 'timelineDay', key: `timeline_day_${index}`, text: dayText, position });
      items.push({
        kind: 'timelineDayContent',
        key: `timeline_day_content_${index}`,
        image: day.image,
        subTitleHtml: itinerary.customDayTitles ? null : day.title,
        bodyHtml: day.description,
        footerHtml: day.footer,
        position,
      });
    });
  }

  // 7 — footer
  if (itinerary.footer) {
    items.push({ kind: 'body', key: 'footer', html: itinerary.footer });
  }

  // Bottom bar. mapPrice is called with unit = null here — the unit rides in `caption`
  // rather than being appended to the price, unlike the activity screens.
  const price = mapPrice(currency, itinerary.costPrefix, itinerary.cost, null);
  const caption = itinerary.costUnit?.trim() || null;
  const hasContent = price !== null || caption !== null;
  const buttonText = itinerary.ctaButtonText;

  const bottomModel: ItineraryBottomBar | null =
    hasContent || buttonText
      ? { title: price, caption, buttonText, buttonPlacement: hasContent ? 'end' : 'center' }
      : null;

  // A catalog itinerary never carries activities: Itinerary_DaySerializer hard-codes
  // timeslots -> []. Stated once so nobody reads the day list as "the schedule".
  notes.push(
    'A catalog itinerary carries day titles, prose and images only — never activities. ' +
      'The day-by-day schedule lives on the tour.',
  );

  return {
    title: itinerary.name,
    items,
    bottomModel,
    showShare: itinerary.shareText !== null,
    notes,
  };
}
